import type { PeriodRange } from "@components/PeriodFilter";
import dayjs from "dayjs";
import { create } from "zustand";

export type NetworkTelemetryScope = "object" | "channel" | "group";
export type NetworkTelemetryMetric = "kw" | "kvar" | "i";
export type CoefficientMode = "with" | "without";

interface NetworkTelemetryFilters {
  scope: NetworkTelemetryScope;
  metric: NetworkTelemetryMetric;
  interval: number;
  coefficient: CoefficientMode;
  selectedChannel?: number;
  selectedGroup?: string;
  selectedGroupChannel: number | null;
  period: PeriodRange;
}

interface NetworkTelemetryActions {
  setScope: (scope: NetworkTelemetryScope) => void;
  setMetric: (metric: NetworkTelemetryMetric) => void;
  setInterval: (interval: number) => void;
  setCoefficient: (mode: CoefficientMode) => void;
  setSelectedChannel: (channel?: number) => void;
  setSelectedGroup: (group?: string) => void;
  setSelectedGroupChannel: (channel: number | null) => void;
  setPeriod: (period: PeriodRange) => void;
  resetChannels: () => void;
}

type NetworkTelemetryStore = NetworkTelemetryFilters & NetworkTelemetryActions;

const initialPeriod = (): PeriodRange => {
  const start = dayjs().startOf("day");
  return { from: start, to: start.add(1, "day") };
};

export const useNetworkTelemetryStore = create<NetworkTelemetryStore>()((set) => ({
  scope: "object",
  metric: "kw",
  interval: 5,
  coefficient: "with",
  selectedChannel: undefined,
  selectedGroup: undefined,
  selectedGroupChannel: null,
  period: initialPeriod(),

  setScope: (scope) =>
    set({
      scope,
      selectedChannel: undefined,
      selectedGroup: undefined,
      selectedGroupChannel: null,
    }),

  setMetric: (metric) => set({ metric }),
  setInterval: (interval) => set({ interval }),
  setCoefficient: (coefficient) => set({ coefficient }),

  setSelectedChannel: (selectedChannel) => set({ selectedChannel }),

  setSelectedGroup: (selectedGroup) => set({ selectedGroup, selectedGroupChannel: null }),

  setSelectedGroupChannel: (selectedGroupChannel) => set({ selectedGroupChannel }),
  setPeriod: (period) => set({ period }),
  resetChannels: () =>
    set({ selectedChannel: undefined, selectedGroup: undefined, selectedGroupChannel: null }),
}));
